import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/PageHeader";
import { api, errMsg, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Billing() {
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const biz = user?.business || {};

  useEffect(() => {
    refresh();
    api.get("/public/plans").then((r) => setPlans(r.data));
  }, [refresh]);

  const choose = async (p) => {
    setBusy(p.id);
    try {
      const { data } = await api.post("/payments/checkout", { plan_id: p.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(null);
    }
  };

  return (
    <div>
      <PageHeader title="Billing" subtitle="Your StaffRotas subscription (GBP)" />
      <div className="mb-8 rounded-xl border border-slate-200 bg-white p-6" data-testid="billing-current">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Current plan</p>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <p className="font-display text-2xl font-bold" data-testid="billing-plan-name">{biz.plan?.name || "—"}</p>
          <Badge data-testid="billing-plan-status">{biz.plan_status}</Badge>
        </div>
        {biz.sandbox ? (
          <p className="mt-3 text-sm text-indigo-700" data-testid="billing-demo-note">This is the live demo, so billing is switched off. <a href="/signup" className="font-semibold underline" data-testid="billing-demo-signup-link">Start free</a> to set up your own business.</p>
        ) : biz.free_account && (
          <p className="mt-3 flex items-center gap-2 text-sm text-amber-700" data-testid="billing-free-note"><Gift className="h-4 w-4" />Complimentary account provided by StaffRotas — no payment needed.</p>
        )}
        {biz.plan_status === "pending_payment" && <p className="mt-3 text-sm text-slate-600">Complete checkout below to activate your chosen plan.</p>}
      </div>
      {!biz.free_account && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {plans.filter((p) => p.price > 0).map((p) => {
            const current = biz.plan_id === p.id && biz.plan_status === "active";
            return (
              <div key={p.id} className={`flex flex-col rounded-xl border bg-white p-6 ${p.highlighted ? "border-indigo-600" : "border-slate-200"}`} data-testid={`billing-plan-${p.code}`}>
                <p className="font-display text-xl font-bold">{p.name}</p>
                <p className="mt-2 font-display text-3xl font-extrabold">{money(p.price)}<span className="text-sm font-medium text-slate-400">/mo</span></p>
                <ul className="mt-4 flex-1 space-y-1.5 text-sm">{p.features.map((f) => <li key={f} className="flex gap-2"><Check className="h-4 w-4 text-emerald-500" />{f}</li>)}</ul>
                <Button className="mt-6 rounded-full" disabled={current || busy === p.id} onClick={() => choose(p)} data-testid={`stripe-checkout-btn-${p.code}`}>
                  {current ? "Current plan" : busy === p.id ? "Redirecting…" : "Subscribe"}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
